import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import AdjustIcon from '@mui/icons-material/Adjust';
import PropTypes from 'prop-types';
const getDpiFromBuffer = (buffer) => {
  const view = new DataView(buffer);
  
  // Check if JPEG
  if (view.byteLength >= 2 && view.getUint16(0) === 0xFFD8) {
    let offset = 2;
    while (offset < view.byteLength) {
      const marker = view.getUint16(offset);
      if (marker === 0xFFE0) { // APP0
        if (offset + 16 <= view.byteLength) {
          if (
            view.getUint8(offset + 4) === 0x4A && // J
            view.getUint8(offset + 5) === 0x46 && // F
            view.getUint8(offset + 6) === 0x49 && // I
            view.getUint8(offset + 7) === 0x46 && // F
            view.getUint8(offset + 8) === 0x00
          ) {
            const units = view.getUint8(offset + 11);
            const xDensity = view.getUint16(offset + 12);
            if (units === 1) { // dpi
              return `${xDensity} dpi`;
            } else if (units === 2) { // dpcm
              return `${Math.round(xDensity * 2.54)} dpi`;
            }
          }
        }
        break;
      }
      if (marker === 0xFFDA || marker === 0xFFD9) break;
      offset += 2 + view.getUint16(offset + 2);
    }
  }
  
  // Check if PNG
  if (
    view.byteLength >= 8 &&
    view.getUint32(0) === 0x89504E47 &&
    view.getUint32(4) === 0x0D0A1A0A
  ) {
    let offset = 8;
    while (offset + 17 <= view.byteLength) {
      const length = view.getUint32(offset);
      const type = view.getUint32(offset + 4);
      
      if (type === 0x70485973) { // pHYs
        const xPixelsPerMeter = view.getUint32(offset + 8);
        const unitSpecifier = view.getUint8(offset + 16);
        if (unitSpecifier === 1) { // meter
          const dpi = Math.round(xPixelsPerMeter * 0.0254);
          return `${dpi} dpi`;
        }
        break;
      }
      
      offset += 12 + length;
    }
  }

  return '72 dpi';
};

const ImagePreviewCard = ({ image }) => {
  const [hovered, setHovered] = useState(false);
  const [src, setSrc] = useState('');
  const [metadata, setMetadata] = useState({
    name: 'N/A',
    size: 'N/A',
    type: 'N/A',
    dimensions: 'N/A',
    resolution: '72 dpi',
  });

  useEffect(() => {
    if (!image) {
      setSrc('');
      return;
    }

    let isMounted = true;
    let objectUrl = '';

    if (image instanceof File) {
      objectUrl = URL.createObjectURL(image);
      setSrc(objectUrl);
    } else {
      setSrc(encodeURI(image));
    }

    const updateMetadata = async () => {
      let name = 'N/A';
      let size = 'N/A';
      let type = 'N/A';
      let dimensions = 'N/A';
      let resolution = '72 dpi';

      const currentSrc = objectUrl || encodeURI(image);

      if (image instanceof File) {
        name = image.name;
        
        // Format size
        const bytes = image.size;
        if (bytes < 1024 * 1024) {
          size = `${(bytes / 1024).toFixed(1)} Kb`;
        } else {
          size = `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
        }

        // Format type
        if (image.type) {
          const parts = image.type.split('/');
          const rawType = parts[parts.length - 1];
          type = rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();
        } else {
          const ext = image.name.split('.').pop();
          type = ext ? ext.charAt(0).toUpperCase() + ext.slice(1).toLowerCase() : 'N/A';
        }
        if (type === 'Jpg') type = 'Jpeg';

        // Read dimensions
        try {
          const imgObj = new Image();
          imgObj.src = currentSrc;
          await new Promise((resolve) => {
            imgObj.onload = () => {
              dimensions = `${imgObj.naturalWidth} × ${imgObj.naturalHeight} pixels`;
              resolve();
            };
            imgObj.onerror = () => resolve();
          });
        } catch (err) {
          console.error("Failed to load local image dimensions", err);
        }

        // Read resolution (DPI)
        try {
          const buffer = await image.arrayBuffer();
          resolution = getDpiFromBuffer(buffer);
        } catch (err) {
          console.error("Failed to load local image resolution", err);
        }
      } else if (typeof image === 'string') {
        // String URL - Extract filename
        try {
          const decoded = decodeURIComponent(image);
          const urlParts = decoded.split('/');
          const filename = urlParts[urlParts.length - 1];
          name = filename.split('?')[0];
        } catch (e) {
          name = image.substring(image.lastIndexOf('/') + 1);
        }

        // Infer type from extension
        const extMatch = name.match(/\.([a-zA-Z0-9]+)$/);
        const ext = extMatch ? extMatch[1] : '';
        type = ext ? ext.charAt(0).toUpperCase() + ext.slice(1).toLowerCase() : 'N/A';
        if (type === 'Jpg') type = 'Jpeg';

        // Load dimensions
        try {
          const imgObj = new Image();
          imgObj.src = image;
          await new Promise((resolve) => {
            imgObj.onload = () => {
              dimensions = `${imgObj.naturalWidth} × ${imgObj.naturalHeight} pixels`;
              resolve();
            };
            imgObj.onerror = () => resolve();
          });
        } catch (err) {
          console.error("Failed to load remote image dimensions", err);
        }

        // Try to fetch arrayBuffer for resolution (DPI)
        try {
          const response = await fetch(image);
          const buffer = await response.arrayBuffer();
          resolution = getDpiFromBuffer(buffer);
        } catch (err) {
          // ignore CORS or fetch errors
        }

        // Try to fetch content-length for file size
        try {
          const response = await fetch(image, { method: 'HEAD' });
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const bytes = parseInt(contentLength, 10);
            if (bytes < 1024 * 1024) {
              size = `${(bytes / 1024).toFixed(1)} Kb`;
            } else {
              size = `${(bytes / (1024 * 1024)).toFixed(1)} Mb`;
            }
          }
        } catch (err) {
          // HEAD request might fail due to CORS, show N/A
        }
      }

      if (isMounted) {
        setMetadata({
          name,
          size,
          type,
          dimensions,
          resolution,
        });
      }
    };

    updateMetadata();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [image]);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        height: '200px',
        borderRadius: '4px',
        overflow: 'hidden',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
      }}
    >
      <img
        src={src}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'filter 0.3s ease',
          filter: hovered ? 'blur(4px)' : 'none',
        }}
        alt="Preview"
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'stretch',
          padding: '16px',
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateY(0)' : 'translateY(10px)',
          transition: 'opacity 0.3s ease, transform 0.3s ease',
          color: '#ffffff',
          pointerEvents: 'none',
        }}
      >
        {/* Title / File Name Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 1 }}>
          <InsertDriveFileIcon sx={{ fontSize: '18px', color: '#77CFFE' }} />
          <Typography
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 600,
              fontSize: '13px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1,
            }}
            title={metadata.name}
          >
            {metadata.name}
          </Typography>
        </Box>
        
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.15)', my: 0.5 }} />

        {/* File Size Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <SaveIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>File size</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.size}</Typography>
        </Box>

        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* File Type Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ImageIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>File type</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.type}</Typography>
        </Box>

        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* Dimensions Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AspectRatioIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>Dimensions</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.dimensions}</Typography>
        </Box>
        
        <Box sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', my: 0.5 }} />

        {/* Resolution Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AdjustIcon sx={{ fontSize: '16px', color: '#bdbdbd' }} />
            <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', color: '#bdbdbd' }}>Resolution</Typography>
          </Box>
          <Typography sx={{ fontFamily: 'Poppins', fontSize: '11px', fontWeight: 500 }}>{metadata.resolution}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

ImagePreviewCard.propTypes = {
  image: PropTypes.any,
};

export default ImagePreviewCard;
